import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { deleteTrackComment } from "../api";
import { LEAD_TRACK_ACTIVITY_QUERY_KEY } from "./use-lead-track-activity.hook";
import { ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";

interface UseDeleteTrackCommentOptions {
  trackUId: string;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export const useDeleteTrackComment = ({
  trackUId,
  onSuccess,
  onError,
}: UseDeleteTrackCommentOptions) => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<void>,
    { response?: { data?: { message?: string } } },
    string // commentUId
  >({
    mutationFn: (commentUId) => deleteTrackComment(commentUId),
    onSuccess: () => {
      // Invalidate activity query to refresh the feed
      queryClient.invalidateQueries({ 
        queryKey: [LEAD_TRACK_ACTIVITY_QUERY_KEY, trackUId] 
      });

      showToast({
        type: ToastType.SUCCESS,
        message: "Comment deleted",
        toastId: "delete-track-comment-success",
        options: {
          position: "bottom-left",
          autoClose: 2000,
        },
      });

      onSuccess?.();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete comment";
      
      showToast({
        type: ToastType.ERROR,
        message: errorMessage,
        toastId: "delete-track-comment-error",
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });

      onError?.(error);
    },
  });
};

export default useDeleteTrackComment;

